// The villas an agent has ticked on the search page ("Selected Properties to
// Generate Brochure"), kept across the search <-> property detail round trip.
//
// The selection used to live only in SearchProperty's component state. Opening a
// property is a route change that UNMOUNTS the search page, so the state was
// destroyed and coming back remounted it empty: an agent who ticked three villas,
// opened one to read it, and pressed Back had lost all three. Nothing cleared the
// selection — it simply lived somewhere that does not survive the route change.
//
// sessionStorage, like the scroll position next door (scrollUtil.js): it lasts as
// long as the tab does, survives Back and a refresh, and is gone when the tab is
// closed. localStorage would bring yesterday's ticks back tomorrow.
//
// The stored selection is stamped with the agent who made it and is only handed
// back to that same agent. Sign-out clears it explicitly, but an expired session
// or a token removed some other way does not run that code, and one agent's
// half-built offer must never appear ticked for the next person on the tab.

export const SEARCH_SELECTION_KEY = 'vt_search_selection';

// The brochure holds at most five villas (SearchProperty's onToggleProperty
// enforces the same number). Applied on load too, so a tampered or stale entry
// can never restore more than the UI lets anyone pick.
export const MAX_SELECTED_PROPERTIES = 5;

const idOf = (property) => property?.listing?._id;

const sameOwner = (a, b) =>
  a !== undefined && a !== null && a !== '' && String(a) === String(b);

// Only well-formed, distinct villas, capped at the brochure limit. The cards
// match on listing._id, so an entry without one could never be shown as ticked
// or be unticked again — it would sit in the brochure with no way to remove it.
const clean = (properties) => {
  if (!Array.isArray(properties)) return [];
  const seen = new Set();
  const out = [];
  for (const property of properties) {
    const id = idOf(property);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(property);
    if (out.length === MAX_SELECTED_PROPERTIES) break;
  }
  return out;
};

/** The selection this agent left on the search page, or [] — never throws. */
export const loadSearchSelection = (ownerId) => {
  try {
    const raw = sessionStorage.getItem(SEARCH_SELECTION_KEY);
    if (!raw) return [];
    const stored = JSON.parse(raw);
    if (!stored || !sameOwner(stored.ownerId, ownerId)) return [];
    return clean(stored.properties);
  } catch {
    return [];
  }
};

/** Mirror the current selection. An empty selection removes the entry, so
 *  unticking the last villa does not leave it to come back on the next visit. */
export const saveSearchSelection = (ownerId, properties) => {
  try {
    const list = clean(properties);
    if (!list.length || ownerId === undefined || ownerId === null || ownerId === '') {
      sessionStorage.removeItem(SEARCH_SELECTION_KEY);
      return;
    }
    sessionStorage.setItem(
      SEARCH_SELECTION_KEY,
      JSON.stringify({ ownerId: String(ownerId), properties: list })
    );
  } catch {
    // Storage full or unavailable (private mode). The selection still works for
    // as long as the page stays mounted; it just will not survive a round trip.
  }
};

export const clearSearchSelection = () => {
  try {
    sessionStorage.removeItem(SEARCH_SELECTION_KEY);
  } catch {}
};
